import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import { SETTINGS_PAGE_MAX_WIDTH } from 'src/constants/uiConsts';
import {
  SettingsPageToolbarContext,
  SettingsPageToolbarContextInterface,
} from 'src/context/settingsPageToolbarContext';
import { SettingsPageRow } from 'src/legacy/components/Settings';
import { SettingsPageToolbar } from 'src/legacy/components/Settings/SettingsPageToolbar';

import { useShell } from '../../AppShell/ShellContext';

export interface SettingsVariantProps {
  children: React.ReactNode;
  /** When true, skips the SettingsPageRow + SettingsPageToolbar wrapper.
   *  Use for pages that provide their own title area (e.g. via ModuleContainer). */
  hideDescription?: boolean;
}

/**
 * Settings page variant — responsive padding (accounts for drawer width on desktop), and wraps
 * children in SettingsPageToolbarContext.Provider for toolbar action injection.
 *
 * Mirrors the legacy SettingsPageContent layout:
 *   xs (≤599px):  padding 40px 16px, overflowY auto
 *   sm (≥600px):  padding 40px 36px
 *   md (≥960px):  paddingLeft = drawerWidth + 80px, paddingTop = 40px
 */
export const SettingsVariant: React.FC<SettingsVariantProps> = ({
  children,
  hideDescription = false,
}) => {
  const { drawerWidth } = useShell();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [toolbarActions, setToolbarActions] =
    React.useState<React.ReactNode>(null);

  const toolbarContextValue =
    React.useMemo<SettingsPageToolbarContextInterface>(
      () => ({ toolbarActions, setToolbarActions }),
      [toolbarActions],
    );

  // Responsive padding matching legacy layout.
  // When hideDescription is true the page provides its own title area (via
  // ModuleContainer) and in legacy went through PageContent's normal grid
  // which uses 0 top/bottom padding and drawerWidth + 36 left padding.
  // Otherwise mirror SettingsPageContent which uses 40px top/bottom and
  // drawerWidth + 80 left padding.
  const paddingTop = hideDescription ? 0 : 40;
  const paddingBottom = hideDescription ? 0 : 40;
  const paddingRight = isSmUp ? 36 : 16;
  const desktopExtraPadding = hideDescription ? 36 : 80;
  const paddingLeft = isMdUp
    ? drawerWidth + desktopExtraPadding
    : isSmUp
      ? 36
      : 16;

  return (
    <div
      className="flex w-full flex-col box-content"
      style={{
        height: 'auto',
        maxWidth: SETTINGS_PAGE_MAX_WIDTH,
        margin: '0 auto',
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
        ...(isSmUp ? {} : { overflowY: 'auto' as const }),
      }}
    >
      <SettingsPageToolbarContext.Provider value={toolbarContextValue}>
        {hideDescription ? (
          children
        ) : (
          <SettingsPageRow fullHeight description={<SettingsPageToolbar />}>
            {children}
          </SettingsPageRow>
        )}
      </SettingsPageToolbarContext.Provider>
    </div>
  );
};
